import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ConceptMap from "@/components/ConceptMap";
import LessonQuiz from "@/components/LessonQuiz";
import { LessonCards, VideoComingSoon } from "@/components/LessonCards";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ArrowRight, Check, CheckCircle2 } from "lucide-react";

const STEPS = [
  { key: "video", label: "Video / Sintesi" },
  { key: "mappa", label: "Mappa" },
  { key: "riepilogo", label: "Riepilogo" },
  { key: "quiz", label: "Quiz" },
];

export default function Lezione() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState(false);
  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [flash, setFlash] = useState(false);
  const quizReported = useRef(false);

  useEffect(() => {
    setLesson(null);
    setStep(0);
    setCompleted(false);
    quizReported.current = false;
    api
      .get(`/lessons/${lessonId}`)
      .then((r) => {
        setLesson(r.data);
        setCompleted(r.data.completed);
        api.post("/progress/visit", { lessonId }).catch(() => {});
      })
      .catch(() => setError(true));
  }, [lessonId]);

  if (error) {
    return (
      <div className="pt-10 text-center">
        <p className="font-bold">Lezione non trovata.</p>
        <Link to="/moduli" className="mt-3 inline-block underline text-sm">Torna ai moduli</Link>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="pt-6 space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-64 w-full rounded-3xl" />
      </div>
    );
  }

  const onQuizFinished = (correct, total) => {
    if (quizReported.current || total === 0) return;
    quizReported.current = true;
    api.post(`/lessons/${lessonId}/quiz-result`, { correct, total }).catch(() => {});
  };

  const markComplete = async () => {
    setCompleting(true);
    try {
      await api.post(`/lessons/${lessonId}/complete`);
      setCompleted(true);
      setFlash(true);
      toast.success("Lezione completata.");
      setTimeout(() => setFlash(false), 700);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCompleting(false);
    }
  };

  // ---------- LEZIONE A SCHEDE TIPIZZATE (nuovo formato, es. Medicina) ----------
  if (lesson.type === "cards") {
    const isDraft = lesson.cardsStatus === "draft";
    return (
      <div className="pt-6">
        {flash && <div className="volt-flash fixed inset-0 z-50 bg-[color:var(--volt)]/70" aria-hidden="true" />}

        {/* Watermark anti-condivisione */}
        {user?.email && (
          <div className="pointer-events-none select-none fixed inset-0 z-30 overflow-hidden" aria-hidden="true" data-testid="lesson-watermark">
            <div className="absolute -inset-[25%] flex flex-wrap content-start gap-x-20 gap-y-24 rotate-[-18deg] opacity-[0.05]">
              {Array.from({ length: 80 }).map((_, i) => (
                <span key={i} className="font-mono text-sm font-bold whitespace-nowrap text-[color:var(--ink)]">
                  {user.email}
                </span>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => navigate(`/moduli/${lesson.moduleId}`)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--text-2)] hover:text-[color:var(--ink)] transition-colors duration-200"
          data-testid="lesson-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> {lesson.module?.titolo}
        </button>

        <div className="mt-3 fade-up d1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] tracking-widest uppercase border border-[color:var(--hairline)] rounded-full px-2 py-0.5">
              Blocco {lesson.blocco || lesson.module?.blocco}
            </span>
            {isDraft && (
              <span
                className="text-[10px] rounded-full px-2 py-0.5 font-extrabold uppercase tracking-wide text-white"
                style={{ background: "#B45309" }}
                data-testid="lesson-draft-badge"
              >
                Bozza — solo admin
              </span>
            )}
            {completed && (
              <span className="text-[10px] rounded-full bg-[color:var(--volt)] px-2 py-0.5 font-extrabold uppercase tracking-wide" data-testid="lesson-completed-badge">
                Completata
              </span>
            )}
          </div>
          <h1 className="mt-2.5 text-2xl md:text-3xl font-extrabold tracking-tight leading-tight" data-testid="lesson-title">
            {lesson.titolo}
          </h1>
          <p className="mt-1.5 font-mono text-xs text-[color:var(--text-2)]">
            Video{lesson.duration ? ` ${lesson.duration}` : ""} in arrivo · {(lesson.cards || []).length} schede di ripasso
          </p>
        </div>

        {/* Video placeholder */}
        <div className="mt-5 fade-up d2">
          <VideoComingSoon duration={lesson.duration} />
        </div>

        {/* Schede tipizzate */}
        <div className="mt-5">
          <LessonCards cards={lesson.cards} />
        </div>

        {/* Completa + prossima lezione */}
        <div className="mt-8 rounded-3xl bg-[color:var(--ink)] text-white p-5 fade-up">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-extrabold tracking-tight">{completed ? "Lezione completata." : "Hai studiato le schede?"}</p>
              <p className="text-xs text-white/60 mt-1">{completed ? "Puoi passare alla prossima." : "Segna la lezione come completata."}</p>
            </div>
            {!completed ? (
              <button
                onClick={markComplete}
                disabled={completing}
                data-testid="lesson-complete-button"
                className="press shrink-0 inline-flex items-center gap-2 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-5 py-3 min-h-[44px] hover:brightness-95 disabled:opacity-60 transition-all duration-200"
              >
                <Check className="w-4 h-4" strokeWidth={3} /> Completa
              </button>
            ) : (
              <CheckCircle2 className="w-8 h-8 shrink-0 text-[color:var(--volt)]" data-testid="lesson-completed-icon" />
            )}
          </div>
          {lesson.nextLesson && (
            <button
              onClick={() => navigate(`/lezione/${lesson.nextLesson.id}`)}
              data-testid="lesson-next-lesson-button"
              className="press mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-bold text-sm px-5 py-3 min-h-[44px] hover:bg-white/10 transition-colors duration-200"
            >
              Prossima lezione: <span className="truncate max-w-[180px]">{lesson.nextLesson.titolo}</span> <ArrowRight className="w-4 h-4 shrink-0" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---------- SCHEDA DI STUDIO (guida) ----------
  if (lesson.type === "scheda") {
    return (
      <div className="pt-6">
        {flash && <div className="volt-flash fixed inset-0 z-50 bg-[color:var(--volt)]/70" aria-hidden="true" />}

        {/* Watermark anti-condivisione: email utente in sovrimpressione tenue */}
        {user?.email && (
          <div className="pointer-events-none select-none fixed inset-0 z-30 overflow-hidden" aria-hidden="true" data-testid="lesson-watermark">
            <div className="absolute -inset-[25%] flex flex-wrap content-start gap-x-20 gap-y-24 rotate-[-18deg] opacity-[0.05]">
              {Array.from({ length: 80 }).map((_, i) => (
                <span key={i} className="font-mono text-sm font-bold whitespace-nowrap text-[color:var(--ink)]">
                  {user.email}
                </span>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => navigate(`/moduli/${lesson.moduleId}`)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--text-2)] hover:text-[color:var(--ink)] transition-colors duration-200"
          data-testid="lesson-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> {lesson.module?.titolo}
        </button>

        <div className="mt-3 fade-up d1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] tracking-widest uppercase border border-[color:var(--hairline)] rounded-full px-2 py-0.5">
              Blocco {lesson.blocco || lesson.module?.blocco}
            </span>
            <span className="font-mono text-[10px] tracking-widest uppercase rounded-full bg-[color:var(--bg-subtle)] px-2 py-0.5 text-[color:var(--text-2)]">
              Scheda di studio
            </span>
            {completed && (
              <span className="text-[10px] rounded-full bg-[color:var(--volt)] px-2 py-0.5 font-extrabold uppercase tracking-wide" data-testid="lesson-completed-badge">
                Completata
              </span>
            )}
          </div>
          <h1 className="mt-2.5 text-2xl md:text-3xl font-extrabold tracking-tight leading-tight" data-testid="lesson-title">
            {lesson.titolo}
          </h1>
          <p className="mt-1.5 font-mono text-xs text-[color:var(--text-2)]" data-testid="lesson-scheda-subtitle">
            {lesson.hours ? `${lesson.hours} ore` : ""}{lesson.hours && (lesson.blockName || lesson.module?.titolo) ? " · " : ""}{lesson.blockName || ""}
          </p>
        </div>

        {/* Intro */}
        {lesson.intro && (
          <p className="mt-5 text-[15px] leading-7 fade-up d2" data-testid="lesson-scheda-intro">
            {lesson.intro}
          </p>
        )}

        {/* Sections */}
        <div className="mt-6 space-y-6">
          {(lesson.sections || []).map((sec, i) => (
            <section key={i} className="fade-up" style={{ animationDelay: `${Math.min(i, 6) * 40}ms` }} data-testid="lesson-scheda-section">
              <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase">{sec.heading}</h2>
              <div className="mt-3 rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
                <ul className="space-y-2.5">
                  {(sec.points || []).map((p, j) => (
                    <li key={j} className="flex gap-3 text-sm leading-6">
                      <span className="mt-[9px] shrink-0 w-1.5 h-1.5 rounded-full bg-[color:var(--ink)]" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                {sec.key && (
                  <div className="mt-4 rounded-2xl bg-[color:var(--volt)]/25 border border-[color:var(--volt)] p-4" data-testid="lesson-scheda-key">
                    <p className="font-mono text-[10px] tracking-widest uppercase font-bold mb-1">Da ricordare</p>
                    <p className="text-sm font-semibold leading-6">{sec.key}</p>
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>

        {/* Closing + complete */}
        <div className="mt-8 rounded-3xl bg-[color:var(--ink)] text-white p-5 fade-up">
          {lesson.closing && (
            <p className="text-sm leading-6 text-white/80" data-testid="lesson-scheda-closing">{lesson.closing}</p>
          )}
          <div className={`flex items-center justify-between gap-4 ${lesson.closing ? "mt-4 pt-4 border-t border-white/10" : ""}`}>
            <div>
              <p className="font-extrabold tracking-tight">{completed ? "Scheda completata." : "Hai studiato la scheda?"}</p>
              <p className="text-xs text-white/60 mt-1">{completed ? "Ora mettiti alla prova." : "Segnala come completata e verifica con le simulazioni."}</p>
            </div>
            {!completed ? (
              <button
                onClick={markComplete}
                disabled={completing}
                data-testid="lesson-complete-button"
                className="press shrink-0 inline-flex items-center gap-2 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-5 py-3 min-h-[44px] hover:brightness-95 disabled:opacity-60 transition-all duration-200"
              >
                <Check className="w-4 h-4" strokeWidth={3} /> Completa
              </button>
            ) : (
              <CheckCircle2 className="w-8 h-8 shrink-0 text-[color:var(--volt)]" data-testid="lesson-completed-icon" />
            )}
          </div>
          <button
            onClick={() => navigate("/simulazioni")}
            data-testid="lesson-goto-simulations-button"
            className="press mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-bold text-sm px-5 py-3 min-h-[44px] hover:bg-white/10 transition-colors duration-200"
          >
            Verifica sul Blocco {lesson.blocco || lesson.module?.blocco} <ArrowRight className="w-4 h-4 shrink-0" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-6">
      {flash && <div className="volt-flash fixed inset-0 z-50 bg-[color:var(--volt)]/70" aria-hidden="true" />}

      <button
        onClick={() => navigate(`/moduli/${lesson.moduleId}`)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--text-2)] hover:text-[color:var(--ink)] transition-colors duration-200"
        data-testid="lesson-back-button"
      >
        <ArrowLeft className="w-4 h-4" /> {lesson.module?.titolo}
      </button>

      <div className="mt-3 fade-up">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] tracking-widest uppercase border border-[color:var(--hairline)] rounded-full px-2 py-0.5">
            Blocco {lesson.module?.blocco}
          </span>
          {lesson.isExample && (
            <span className="text-[10px] rounded-full bg-[color:var(--bg-subtle)] text-[color:var(--text-2)] border border-[color:var(--hairline)] px-2 py-0.5 font-semibold uppercase tracking-wide" data-testid="lesson-example-badge">
              Contenuto di esempio
            </span>
          )}
          {completed && (
            <span className="text-[10px] rounded-full bg-[color:var(--volt)] px-2 py-0.5 font-extrabold uppercase tracking-wide" data-testid="lesson-completed-badge">
              Completata
            </span>
          )}
        </div>
        <h1 className="mt-2.5 text-2xl md:text-3xl font-extrabold tracking-tight leading-tight" data-testid="lesson-title">
          {lesson.titolo}
        </h1>
      </div>

      {/* Step navigation */}
      <div className="sticky top-14 z-30 bg-white/90 backdrop-blur -mx-4 px-4 py-3 mt-4 border-b border-[color:var(--hairline)]">
        <div className="flex gap-1.5" data-testid="lesson-steps-nav">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              data-testid={`lesson-step-${s.key}`}
              aria-current={step === i ? "step" : undefined}
              className={`press flex-1 rounded-full px-2 py-2 min-h-[44px] text-[11px] font-bold uppercase tracking-wide transition-colors duration-200 ${
                step === i
                  ? "bg-[color:var(--ink)] text-white"
                  : i < step
                  ? "bg-[color:var(--volt)]/40 text-[color:var(--ink)]"
                  : "bg-[color:var(--bg-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--ink)]"
              }`}
            >
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 min-h-[300px]">
        {/* 1 — VIDEO / SINTESI */}
        {step === 0 && (
          <div className="fade-up" data-testid="lesson-part-video">
            <div className="rounded-3xl overflow-hidden border border-[color:var(--hairline)] bg-black">
              {lesson.urlVideo ? (
                <video controls preload="metadata" className="w-full aspect-video" data-testid="lesson-video-player">
                  <source src={lesson.urlVideo} type="video/mp4" />
                  Il tuo browser non supporta il video.
                </video>
              ) : (
                <div className="aspect-video flex items-center justify-center text-white/60 text-sm">Video in arrivo.</div>
              )}
            </div>
            <p className="mt-2 font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-2)]">Video segnaposto — sarà sostituito dal contenuto reale</p>
            <div className="mt-5 rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
              <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase mb-3">Sintesi</h2>
              <p className="text-sm md:text-[15px] leading-7" data-testid="lesson-summary-text">{lesson.testoSintesi}</p>
            </div>
          </div>
        )}

        {/* 2 — MAPPA */}
        {step === 1 && (
          <div className="fade-up" data-testid="lesson-part-mappa">
            <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase mb-4">Mappa concettuale</h2>
            <ConceptMap mappa={lesson.mappaConcettuale} />
          </div>
        )}

        {/* 3 — RIEPILOGO */}
        {step === 2 && (
          <div className="fade-up" data-testid="lesson-part-riepilogo">
            <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase mb-1">Scheda riepilogo</h2>
            <p className="text-xs text-[color:var(--text-2)] mb-4">I punti “da domanda”. Memorizzali.</p>
            <div className="space-y-2.5">
              {(lesson.riepilogo || []).map((punto, i) => (
                <div key={i} className="flex gap-3 rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5">
                  <span className="font-mono text-xs font-semibold text-[color:var(--text-2)] pt-0.5 shrink-0 w-6">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-sm leading-6">{punto}</p>
                </div>
              ))}
              {(!lesson.riepilogo || lesson.riepilogo.length === 0) && (
                <p className="text-sm text-[color:var(--text-2)]">Riepilogo in preparazione.</p>
              )}
            </div>
          </div>
        )}

        {/* 4 — QUIZ */}
        {step === 3 && (
          <div className="fade-up" data-testid="lesson-part-quiz">
            <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase mb-4">Quiz di lezione</h2>
            <div className="rounded-3xl border border-[color:var(--hairline)] bg-white p-5">
              <LessonQuiz questions={lesson.questions} onFinished={onQuizFinished} />
            </div>

            <div className="mt-6 rounded-3xl bg-[color:var(--ink)] text-white p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-extrabold tracking-tight">{completed ? "Lezione completata." : "Hai finito?"}</p>
                  <p className="text-xs text-white/60 mt-1">{completed ? "Puoi passare alla prossima." : "Segna la lezione come completata."}</p>
                </div>
                {!completed ? (
                  <button
                    onClick={markComplete}
                    disabled={completing}
                    data-testid="lesson-complete-button"
                    className="press shrink-0 inline-flex items-center gap-2 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-5 py-3 min-h-[44px] hover:brightness-95 disabled:opacity-60 transition-all duration-200"
                  >
                    <Check className="w-4 h-4" strokeWidth={3} /> Completa
                  </button>
                ) : (
                  <CheckCircle2 className="w-8 h-8 shrink-0 text-[color:var(--volt)]" data-testid="lesson-completed-icon" />
                )}
              </div>
              {completed && lesson.nextLesson && (
                <button
                  onClick={() => navigate(`/lezione/${lesson.nextLesson.id}`)}
                  data-testid="lesson-next-lesson-button"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-bold text-sm px-5 py-3 hover:bg-white/10 transition-colors duration-200"
                >
                  Prossima lezione: <span className="truncate max-w-[180px]">{lesson.nextLesson.titolo}</span> <ArrowRight className="w-4 h-4 shrink-0" />
                </button>
              )}
              {completed && !lesson.nextLesson && (
                <button
                  onClick={() => navigate(`/moduli/${lesson.moduleId}`)}
                  data-testid="lesson-back-to-module-button"
                  className="mt-4 w-full rounded-full border border-white/20 text-white font-bold text-sm px-5 py-3 hover:bg-white/10 transition-colors duration-200"
                >
                  Torna al modulo
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Prev / Next step */}
      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          data-testid="lesson-prev-step-button"
          className="press inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-white px-5 py-2.5 min-h-[44px] font-semibold text-sm disabled:opacity-40 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" /> Indietro
        </button>
        {step < 3 && (
          <button
            onClick={() => setStep(step + 1)}
            data-testid="lesson-next-step-button"
            className="press inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white px-5 py-2.5 min-h-[44px] font-extrabold text-sm hover:bg-black/85 transition-colors duration-200"
          >
            Avanti <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
